/**
 * ProjectionChartsToggle - Consolidated chart toggle for projections section.
 *
 * Combines CumulativeReturnComparison and ProjectionChart into a single
 * expandable section with a toggle, reducing default page scroll while
 * keeping both charts accessible on demand.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { CumulativeReturnComparison } from './CumulativeReturnComparison';
import { ProjectionChart } from '../charts/ProjectionChart';
import { SectionTitle } from '../Luxury';

interface ProjectionChartsToggleProps {
  projections: any[];
  equityRequired: number;
}

type ChartView = 'cumulative' | 'cashflow';

export function ProjectionChartsToggle({ projections, equityRequired }: ProjectionChartsToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [chartView, setChartView] = useState<ChartView>('cumulative');

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm text-gold hover:text-gold-bright transition-colors font-medium mb-2"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <BarChart3 className="w-4 h-4" />
        Projection Charts
      </button>

      {isOpen && (
        <div className="space-y-3">
          {/* Chart type toggle */}
          <div className="flex gap-1 bg-graphite rounded-lg p-1 w-fit">
            <button
              onClick={() => setChartView('cumulative')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                chartView === 'cumulative'
                  ? 'bg-charcoal text-gold shadow-sm'
                  : 'text-stone-700 hover:text-stone-300'
              }`}
            >
              Cumulative Return
            </button>
            <button
              onClick={() => setChartView('cashflow')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                chartView === 'cashflow'
                  ? 'bg-charcoal text-gold shadow-sm'
                  : 'text-stone-700 hover:text-stone-300'
              }`}
            >
              Cash Flow
            </button>
          </div>

          {/* Chart content */}
          {chartView === 'cumulative' && (
            <CumulativeReturnComparison
              projections={projections}
              equityInvested={equityRequired}
            />
          )}
          {chartView === 'cashflow' && (
            <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
              <SectionTitle>Cash Flow Projections</SectionTitle>
              <div className="mt-4">
                <ProjectionChart projections={projections} showCumulative={true} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
